import { MenuListParams } from "../../../api/services/client/models";
import {
  CoachDetail,
  IdNameList,
  POData,
  TaxDetail,
} from "../../../api/services/common/models";
import {
  IntakeDetail,
  ProgramExpenseData,
  ProgramStatus,
  SalesCreditDetail,
} from "../../../api/services/program/models";
import { BasicUserDetails } from "../../../api/services/reports/models";
import {
  BillingConfigurationDetailsFormKeyProps,
  BillingScheduleActivities,
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { StatusBadgeType } from "../../../components/statusBadge/StatusBadge";
import {
  TaxConfigureTableData,
  TaxDetailsFormKeyProps,
} from "../../../components/taxDetails/modelsAndSchemas/Models";
import { KeyValue } from "../../../helpers/types/common";
import { CoachAssignmentFormKeys } from "../components/createProgramTabs/tabs/participant/components/coachAssignment/constants";
import { CoachAssignmentDetails } from "../components/createProgramTabs/tabs/participant/components/coachAssignment/model";
import {
  addFeesAndExpensesSiteCoreKeys,
  programSiteCoreKeys,
} from "../siteCore/SiteCoreKeys";

export const CreateProgramFormKey = {
  client: "client",
  deliveryCountry: "deliveryCountry",
  lineOfBusiness: "lineOfBusiness",
  programTemplate: "programTemplate",
  salesOfficeId: "salesOfficeId",
  alternateName: "alternateName",
  isProjectTagged: "isProjectTagged",
  projectKey: "projectKey",
  projectName: "projectName",
  nextPersona: "nextPersona",
  opportunityId: "opportunityId",
  opportunityName: "opportunityName",
  isOpportunityIdValidated: "isOpportunityIdValidated",
} as const;

export interface CreateProgramKeyTypes {
  client: string;
  deliveryCountry: string;
  lineOfBusiness: string;
  programTemplate: string;
  salesOfficeId: string;
  alternateName: string;
  isProjectTagged: boolean;
  projectKey: string;
  projectName: string;
  nextPersona: string;
  opportunityId?: string;
  opportunityName?: string;
  isOpportunityIdValidated?: boolean;
}

export const ProgramTabFormKey = {
  prefix: "prefix",
  firstName: "firstName",
  lastName: "lastName",
  programName: "programName",
  solutionOffering: "solutionOffering",
  programClass: "programClass",
  businessUnit: "businessUnit",
  costCentre: "costCentre",
  clientInformation1: "clientInformation1",
  clientInformation2: "clientInformation2",
  clientInformation3: "clientInformation3",
  site: "site",
  poNumber: "poNumber",
  dossierNumber: "dossierNumber",
  actualClientId: "actualClientId",
  billingOffice: "billingOffice",
  salesOffice: "salesOffice",
  clientRelationShipOwner: "clientRelationShipOwner",
  deliveryOffice: "deliveryOffice",
  billingContact: "billingContact",
  hrContact: "hrContact",
  projectManager: "projectManager",
  projectCoordinator: "projectCoordinator",
  salesCreditDetails: "salesCreditDetails",
  additionalInvoiceContactId: "additionalInvoiceContactId",
  businessApprovalNumber: "businessApprovalNumber",
  additionalHRContactId: "additionalHRContactId",
  deliveryLeadEngagementManagerId: "deliveryLeadEngagementManagerId",
  hiringManager: "hiringManager",
  participantManagerId: "participantManagerId",
} as const;

// API: /programs/{id}
export interface ProgramDetail {
  prefixKey: string;
  firstName: string;
  lastName: string;
  keyNumber: string;
  clientName: string;
  clientCode: string;
  lineOfBusiness: string;
  programTemplate: string;
  registrationEmailId: string;
  registrationCode: string;
  registrationStatus: string;
  deliveryCountry: string;
  deliveryCountryName?: string;
  salesOffice?: string;
  clientId?: string;
  solutionOfferingId?: string;
  programClassId?: string;
  solutionOffering?: string;
  programClass?: string;
  businessUnit?: string;
  costCenter?: string;
  billingOffice?: string;
  countryId?: string;
  lineOfBusinessId?: string;
  programTemplateId?: string;
  programStatus?: string;
  alternateDisplayName: string;
  programStartDate?: string;
  closingReasonTypeKey?: string;
  salesCreditDetails?: SalesCreditDetail[];
  localClientRelationShipOwnerId?: string;
  projectManagerId?: string;
  isProjectTagged?: boolean;
  projectKey?: string;
  projectName?: string;
  isInvoiced?: boolean;
  nextPersona?: string;
  opportunityId?: string;
  opportunityName?: string;
  isOpportunityIdFromBillingConfiguration?: boolean;
  projectId?: string;
  isBillingConfigurationExists?: boolean;
  poNumber?: POData;
  billingContact?: {
    id?: string;
  };
  hrContact?: {
    id?: string;
  };

  solutionOfferingSiteCoreKey?: string;
  isEmployeeIdRequired: boolean;
  isEmployeeBandRequired: boolean;
  specialBillingInstructions?: string;
  billingCountryId?: string;
  outreachEligibilityKeySetting?: string;
  clientInformation1?: string;
  clientInformation2?: string;
  clientInformation3?: string;
  site?: string;
  tmProgramType?: string;

  deliveryOffice?: string;
  hrContactFirstName?: string;
  hrContactLastName?: string;
  socialProgram?: boolean;
  currentPOAmount?: number;
  programName?: string;
  clientRelationshipOwnerFirstName?: string;
  clientRelationshipOwnerLastName?: string;
  projectManagerFirstName?: string;
  projectManagerLastName?: string;
  projectCoordinatorFirstName?: string;
  projectCoordinatorLastName?: string;
  businessApprovalNumber?: string;
  billingFirstName?: string;
  billingLastName?: string;
  versions?: MenuListParams[];
  billingEmail?: string;
  purchaseOrderId?: string;
  clientRelationShipOwnerId?: string;
  deliveryOfficeId?: string;
  billingContactId?: string;
  hrContactId?: string;
  projectCoordinatorId?: string;
  isEditable?: boolean;
  additionalInvoiceContact?: CoachDetail;
  participantManager?: CoachDetail;
  deliveryLeadEngagementManager?: CoachDetail;
  additionalHRContact?: CoachDetail;
  hiringManager?: string;
  salesOfficeId?: string;
  createdDate?: string;
  clientType: string;
  areOpcoFieldsEditable?: boolean;
  dossierNumber?: string;
  actualClientId?: string;
  actualClientName?: string;
  actualClientCode?: string;
  header?: string;
  footer1?: string;
  footer2?: string;
  footer3?: string;
}

export interface ProgramStatusList {
  [key: string]: any;
  id: string;
  status: string;
  fromDate: string | null;
  createdTimeStamp: string;
  createdByName: string;
  notes?: string;
  throughDate?: string | null;
  showEditForReopen?: boolean; // Added to control edit button visibility for reopen status
}
export interface PurchaseOrder {
  poNumber: string;
  purchaseOrderId: string;
  statusId: string;
}
interface Destination {
  countryCode: string;
  countryName: string;
}

interface SolutionOffering {
  id: string;
  name: string;
}

interface CompatibleProgramTemplate {
  id: string;
  name: string;
}

export interface DestinationProgram {
  destination: Destination;
  solutionOfferings: SolutionOffering;
  compatibleProgramTemplate: CompatibleProgramTemplate;
  deliveryOffice?: Destination;
  deliveryOfficeId?: string;
  deliveryOfficeName?: string;
  selectedNextLanguage?: string; //for destination program
}

export interface ParticipantDetail {
  prefixKey: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  genderKey?: string;
  telephoneCountryCode?: string;
  telephone?: string;
  mobileCountryCode?: string;
  mobile?: string;
  appendPhoneCountryCode?: string;
  appendPhone?: string;
  email?: string;
  workEmail?: string;
  spouse?: string;
  educationLevelKey?: string;
  ageRange?: string;
  personalId?: string;
  mustStartByDate?: string;
  actualStartDate?: string;
  primaryLanguageKey?: string;
  socialFund?: string;
  nisid?: string;
  nextContactDate?: string;
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  stateId?: string;
  countryId: string;
  city: string;
  postalCode: string;
  eligibilityKey: string;
  outreachEligibleStatus?: string;
  assignedOutreachConsultant?: string;
  primaryManagedStatus?: string;
  secondaryManagedStatus?: string;
  notificationDate: string;
  releaseDate: string;
  outreachEligibleDate?: string;
  notificationSupportDate?: string;
  notificationSupportStatus: string;
  isContactInADayEnable: boolean;
  companyName?: string;
  jobTitle?: string;
  employeeId?: string;
  employeeBand?: string;
  totalYearsEmployed?: string;
  occupationalFunction?: string;
  occupationalFunctionOther?: string;
  occupationalLevel?: string;
  occupationalLevelOther?: string;
  planPostedDate?: string;
  datePlanApproved?: string;
  currentEmploymentStartDate?: string;
  targetIndustry?: string;
  bestMatchPreference?: string;
  participantGroupType?: string;
  securityClearance: boolean;
  countryName?: string;
  projectedProgramEndDate?: string;
  textSubscribedDate?: string;
  textUnsubscribeDate?: string;
  emailUnsubscribeDate?: string;
  assignedOutreachConsultantLastName?: string;
  assignedOutreachConsultantFirstName?: string;
  currencyName?: string;
  targetJobTitle?: string;
  targetOccupationFunctionId?: string;
  targetOccupationOtherFunction?: string;
  targetOccupationLevelId?: string;
  targetOrganisation?: string;
  targetJobTypeId?: string;
  targetLocation?: string;
  salaryRangeId?: string;
  currenciesId?: string;
  participantNotSeekingEmployment?: boolean;
  participantNotWillngToWorkRemotely?: boolean;
  openToRelocation?: boolean;
  participantWillingToTravelForWork?: boolean;
  experis?: boolean;
  manpowerEngineering?: boolean;
  jeffersonWells?: boolean;
  rpo?: boolean;
  skills?: IdNameList[];
  technicalSkills?: IdNameList[];
  isMarketReady?: boolean;
  marketReadinessDate?: string;
  coachAssignment?: ViewCoachAssignmentDetails;
  aboutParticipantNext?: ParticipantAnswer[];
  isRegisteredInNext?: boolean;
  isTPSBlacklist?: boolean;
  preferredLanguage?: string;
  preferredName?: string;
  participantsContactDetails?: ParticipantsContactDetails;
  nextNotificationPreferences?: NextNotificationPreferences;
  batchTypeKey?: string;
  waveCohort?: string;
  marketName?: string;
  bestMatchPreferenceId?: string;
  targetIndustryId?: string;
  intakeDetails?: IntakeDetail;
}

export interface SmsNotification {
  number: string;
  isPrimary: boolean;
  isSelected: boolean;
}

export interface EmailNotification {
  email: string;
  isPrimary: boolean;
  isSelected: boolean;
}

export interface NextNotificationPreferences {
  isSMSNotificationEnabled: boolean;
  isEmailNotificationEnabled: boolean;
  smsNotification: SmsNotification[];
  emailNotification: EmailNotification[];
}

export interface PhoneDetails {
  contactType: string;
  isoCode: string;
  countryCode: string;
  number: string;
}

export interface EmailDetails {
  emailType: string;
  email: string;
}

export interface AppendNumberDetails {
  isoCode: string;
  countryCode: string;
  number: string;
}

export interface ParticipantsContactDetails {
  contactType1: PhoneDetails;
  contactType2: PhoneDetails;
  emailType1: EmailDetails;
  emailType2: EmailDetails;
  appendNumber: AppendNumberDetails;
}

export interface ViewCoachAssignmentDetails {
  coachAssignmentId: string;
  participantId: string;
  toBeAssignedDate: string;
  coachTypeAssignments: CoachAssignmentDetails[];
}

export interface ProgramTabFormType {
  programName: string;
  solutionOffering: string;
  programClass: string;
  businessUnit: string;
  costCentre: string;
  poNumber: string;
  billingOffice: string;
  salesOffice: string;
  clientRelationShipOwner: string;
  deliveryOffice: string;
  billingContact: string;
  hrContact: string;
  projectManager: string;
  projectCoordinator: string;
  programStatus: string;
  salesCreditDetails?: SalesCreditDetail[];
  firstName?: string;
  lastName?: string;
  prefix?: string;
}
export interface ParticipantAnswer {
  questionId: string; // e.g. "JobSeekerQuestions.TotalYearsOfWorkExperience"
  answerId: string[]; // one or more answers (multi-select possible)
}

export interface ParticipantTabFormType {
  prefix: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  age: string;
  gender?: string;
  spouse: string;
  educationLevel?: string;
  mustStartByDate: string | null;
  primaryLanguage: string;
  socialFund: string;
  nisId: string;
  nextContactDate: string | null;
  projectedProgramEndDate?: string;
  textSubscribedDate: string | null;
  textUnsubscribedDate: string | null;
  emailUnsubscribedDate: string | null;
  batchTypeKey?: string;
  waveCohort?: string;
  address1: string;
  address2: string;
  address3: string;
  countryName: string;
  countryId: string;
  city: string;
  stateProvince?: string;
  postalCode?: string;
  eligibility: string;
  outreachEligibleStatus: string;
  assignedOutreachConsultant: string;
  notificationDate?: string;
  releaseDate: string;
  outreachEligibleDate: string;
  notificationSupportDate?: string;
  notificationSupportStatus?: string;
  primaryManagedStatus: string;
  secondaryManagedStatus: string;
  contactWithin24Hours: boolean;
  clientName: string;
  jobTitle: string;
  employeeId: string | null;
  employeeBand: string | null;
  totalYearsEmployed?: string;
  occupationalFunction?: string;
  occupationalFunctionOther: string;
  occupationalLevel: string;
  occupationalLevelOther: string;
  planPostedDate?: string;
  datePlanApproved: string | null;
  currentEmploymentStartDate?: string;
  bestMatchPreference?: string;
  participantGroupType?: string;
  specialAssignments?: string;
  securityClearence?: boolean;
  ageRange?: string;
  personalId?: string;

  targetJobTitle?: string;
  targetOccupationFunctionId?: string;
  targetOccupationOtherFunction?: string;
  targetOccupationLevelId?: string;
  targetOrganisation?: string;
  targetJobTypeId?: string;
  targetLocation?: string;
  targetIndustryId?: string;
  currenciesId?: string;
  salaryRangeId?: string;
  participantNotSeekingEmployment?: boolean;
  participantNotWillngToWorkRemotely?: boolean;
  openToRelocation?: boolean;
  participantWillingToTravelForWork?: boolean;
  experis?: boolean;
  manpowerEngineering?: boolean;
  jeffersonWells?: boolean;
  rpo?: boolean;
  skills?: string[];
  technicalSkills?: string[];
  participantReadyToStartJobSearch?: string;
  participantJobSearchStartDate?: string;
  coachTypeAssignment?: CoachAssignmentDetails[];
  coachToBeAssignDate: string | null;
  coachAssignmentId?: string;
  isRegisteredInNext?: boolean;
  preferredName: string | null;
  preferredLanguage?: string;
  marketName?: string;
  language?: string;
  aboutParticipantNext?: ParticipantAnswer[];
  isTPSBlacklist?: boolean;

  primaryEmail: string;
  alternativeEmail: string;
  emailType1: string;
  emailType2: string;
  primaryNumber: string;
  primaryCountryCode: string;
  primaryIsoCode: string;
  alternativeNumber: string;
  alternativeCountryCode: string;
  alternativeIsoCode: string;
  concatType1: string;
  concatType2: string;
  appendPhoneNumberCountryCode: string;
  appendPhoneNumberIsoCode: string;
  appendPhoneNumber: string;
  smsCheckBox?: boolean;
  emailCheckBox?: boolean;
  nextSms?: string;
  nextEmail?: string;
  isPrimaryEmail?: boolean;
  isNextRegister?: boolean;
  intakeStatusId?: string;
  biosRequestedCoachIds?: string[];
  biosDeclinedCoachIds?: string[];
  bioStatusId?: string;
  biosSentCoachIds?: string[];
  chemistryMeetingStatusId?: string;
  chemistryMeetingSelectedCoachIds?: string[];
  coachSelectedCoachIds?: string;
  isCoachSelectionDeclined?: boolean;
}

export const EMAIL_TYPE_SITE_CORE_KEY = {
  personalEmail: "EmailIDType.PersonalEmail",
  workEmail: "EmailIDType.WorkEmail",
};

export const PHONE_TYPE_SITE_CORE_KEY = {
  mobile: "PhoneContactType.Mobile",
  work: "PhoneContactType.Work",
  home: "PhoneContactType.Home",
  other: "PhoneContactType.Other",
};

export const ParticipantTabFormKey = {
  prefix: "prefix",
  firstName: "firstName",
  lastName: "lastName",
  preferredName: "preferredName",
  dateOfBirth: "dateOfBirth",
  age: "age",
  gender: "gender",
  spouse: "spouse",
  educationLevel: "educationLevel",
  mustStartByDate: "mustStartByDate",
  actualStartDate: "actualStartDate",
  projectedProgramEndDate: "projectedProgramEndDate",
  primaryLanguage: "primaryLanguage",
  socialFund: "socialFund",
  nisId: "nisId",
  textSubscribedDate: "textSubscribedDate",
  textUnsubscribedDate: "textUnsubscribedDate",
  emailUnsubscribedDate: "emailUnsubscribedDate",
  address1: "address1",
  address2: "address2",
  address3: "address3",
  countryId: "countryId",
  countryName: "countryName",
  city: "city",
  stateProvince: "stateProvince",
  postalCode: "postalCode",
  eligibility: "eligibility",
  outreachEligibleStatus: "outreachEligibleStatus",
  assignedOutreachConsultant: "assignedOutreachConsultant",
  notificationDate: "notificationDate",
  releaseDate: "releaseDate",
  outreachEligibleDate: "outreachEligibleDate",
  notificationSupportDate: "notificationSupportDate",
  notificationSupportStatus: "notificationSupportStatus",
  primaryManagedStatus: "primaryManagedStatus",
  secondaryManagedStatus: "secondaryManagedStatus",
  contactWithin24Hours: "contactWithin24Hours",
  clientName: "clientName",
  jobTitle: "jobTitle",
  employeeId: "employeeId",
  employeeBand: "employeeBand",
  totalYearsEmployed: "totalYearsEmployed",
  occupationalFunction: "occupationalFunction",
  occupationalFunctionOther: "occupationalFunctionOther",
  occupationalLevel: "occupationalLevel",
  occupationalLevelOther: "occupationalLevelOther",
  planPostedDate: "planPostedDate",
  datePlanApproved: "datePlanApproved",
  currentEmploymentStartDate: "currentEmploymentStartDate",
  bestMatchPreference: "bestMatchPreference",
  participantGroupType: "participantGroupType",
  targetJobTitle: "targetJobTitle",
  targetOccupationFunctionId: "targetOccupationFunctionId",
  targetOccupationOtherFunction: "targetOccupationOtherFunction",
  targetOccupationLevelId: "targetOccupationLevelId",
  targetOrganisation: "targetOrganisation",
  targetJobTypeId: "targetJobTypeId",
  targetLocation: "targetLocation",
  targetIndustryId: "targetIndustryId",
  currenciesId: "currenciesId",
  salaryRangeId: "salaryRangeId",
  participantNotSeekingEmployment: "participantNotSeekingEmployment",
  participantNotWillngToWorkRemotely: "participantNotWillngToWorkRemotely",
  openToRelocation: "openToRelocation",
  participantWillingToTravelForWork: "participantWillingToTravelForWork",
  experis: "experis",
  manpowerEngineering: "manpowerEngineering",
  jeffersonWells: "jeffersonWells",
  rpo: "rpo",
  nextContactDate: "nextContactDate",
  skills: "skills",
  technicalSkills: "technicalSkills",
  participantReadyToStartJobSearch: "participantReadyToStartJobSearch",
  participantJobSearchStartDate: "participantJobSearchStartDate",
  coachAssignment: "coachAssignment",
  preferredLanguage: "preferredLanguage",
  tpsStatus: "tpsStatus",
  primaryEmail: "primaryEmail",
  alternativeEmail: "alternativeEmail",
  emailType1: "emailType1",
  emailType2: "emailType2",
  primaryNumber: "primaryNumber",
  primaryCountryCode: "primaryCountryCode",
  primaryIsoCode: "primaryIsoCode",
  alternativeNumber: "alternativeNumber",
  alternativeCountryCode: "alternativeCountryCode",
  alternativeIsoCode: "alternativeIsoCode",
  concatType1: "concatType1",
  concatType2: "concatType2",
  appendPhoneNumberCountryCode: "appendPhoneNumberCountryCode",
  appendPhoneNumberIsoCode: "appendPhoneNumberIsoCode",
  appendPhoneNumber: "appendPhoneNumber",
  smsCheckBox: "smsCheckBox",
  emailCheckBox: "EmailCheckBox",
  nextSms: "nextSms",
  nextEmail: "nextEmail",
  isPrimaryEmail: "isPrimaryEmail",
  isNextRegister: "isNextRegister",
  ageRange: "ageRange",
  personalId: "personalId",
  batchTypeKey: "batchTypeKey",
  waveCohort: "waveCohort",
  intakeStatusId: "intakeStatusId",
  biosRequestedCoachIds: "biosRequestedCoachIds",
  biosDeclinedCoachIds: "biosDeclinedCoachIds",
  bioStatusId: "bioStatusId",
  biosSentCoachIds: "biosSentCoachIds",
  chemistryMeetingStatusId: "chemistryMeetingStatusId",
  chemistryMeetingSelectedCoachIds: "chemistryMeetingSelectedCoachIds",
  coachSelectedCoachIds: "coachSelectedCoachIds",
  isCoachSelectionDeclined: "isCoachSelectionDeclined",
} as const;

export const AddActivityFormKey = {
  chooseParticipant: "chooseParticipant",
  activityType: "activityType",
  modality: "modality",
  topic: "topic",
  notes: "notes",
  onBehalfOfCoachId: "onBehalfOfCoachId",
  actualDurationInMinutes: "actualDurationInMinutes",
  defaultDuration: "defaultDuration",
  activityDate: "activityDate",
} as const;

export interface AddActivityFormProps {
  chooseParticipant: string;
  id?: string;
  activityType: string;
  modality: string;
  topic: string[];
  notes: string;
  onBehalfOfCoachId: string;
  actualDurationInMinutes: string;
  defaultDuration: string;
  activityDate: string;
}

export interface ParticipantActivityTimelineFilterFormType {
  show: string;
  fromDate: string;
  toDate: string;
}

export interface ParticipantActivityTimelineParams {
  id: string;
  isFromGenericParticipant?: boolean;
  isFromNISParticipant?: boolean;
}

export interface ParticipantActivityResponse {
  id: string;
  activityType?: string;
  activityName?: string;
  modality?: string;
  topics?: IdNameList[];
  notes?: string;
  title: string;
  isExpandable: boolean;
  createdDate: string;
  createdBy: string;
  programTemplateName?: string;
  transferredCountryName?: string;
  solutionOfferingsName?: string;
  isEditable: boolean;
  timelineType: number;
  primaryClosingReason?: string;
  secondaryClosingReason?: string;
  activityDate: string;
  defaultDurationInMinutes?: number;
  actualDurationInMinutes?: string;
  onBehalfOfCoach?: BasicUserDetails;
  program?: {
    id: string;
    keyNumber: string;
  };
}

export const ShowFilterAllKey = "ShowFilter.All";
export const showFilterThisMonthKey = "ShowFilter.ThisMonth";
export const ShowFilterLastMonthKey = "ShowFilter.LastMonth";
export const ShowFilterDurationKey = "ShowFilter.Duration";
export const AcrossCountry =
  "Participant Transfer - Across country transfers (Participant Transferred)";
export const WithInTheCountry =
  "Change Program - For CM to OP change program process (Mobility to Outplacement)";

export const ParticipantActivityTimelineFilterFormKey = {
  show: "show",
  fromDate: "fromDate",
  toDate: "toDate",
} as const;

export const OUTPLACEMENT = "Outplacement";
export const ClientManaged = "OutreachEligibility.ClientManaged";
export const OutreachEligible = "OutreachEligibility.OutreachEligible";
export const VirtualNotification =
  "NotificationSupportStatusList.VirtualNotification";
export const OnsiteNotification =
  "NotificationSupportStatusList.OnsiteNotification";

export const PrimaryManagedStatusKeys = {
  ClientManagedNoOutreachPermitted:
    "PrimaryManagedStatus.ClientManaged-NoOutreachPermitted",
  ClientManagedPendingFurtherActions:
    "PrimaryManagedStatus.ClientManaged-PendingFurtherActions",
  ClientManagedPendingORE: "PrimaryManagedStatus.ClientManaged-PendingORE",
};

export const OccupationalLevelOtherKey = "OccupationalLevel.Other";
export const OccupationalFunctionOtherKey = "OccupationalFunction.Other";

export interface AssignedOutreachConsultantResponse {
  id: string;
  firstName: string;
  lastName: string;
  rightOffice: string;
}

export type ParticipantSectionKey =
  | "participantInformation"
  | "addressDetails"
  | "outreach"
  | "sponsoringEmployer"
  | "careerGoals"
  | "participantActivityTimeline";

export const createProgramProgramTabDefaultValue = {
  [ProgramTabFormKey.prefix]: "",
  [ProgramTabFormKey.firstName]: "",
  [ProgramTabFormKey.lastName]: "",
  [ProgramTabFormKey.programName]: "",
  [ProgramTabFormKey.solutionOffering]: "",
  [ProgramTabFormKey.programClass]: "",
  [ProgramTabFormKey.businessUnit]: "",
  [ProgramTabFormKey.costCentre]: "",
  [ProgramTabFormKey.clientInformation1]: "",
  [ProgramTabFormKey.clientInformation2]: "",
  [ProgramTabFormKey.clientInformation3]: "",
  [ProgramTabFormKey.site]: "",
  [ProgramTabFormKey.poNumber]: "",
  [ProgramTabFormKey.billingOffice]: "",
  [ProgramTabFormKey.salesOffice]: "",
  [ProgramTabFormKey.clientRelationShipOwner]: "",
  [ProgramTabFormKey.deliveryOffice]: "",
  [ProgramTabFormKey.billingContact]: "",
  [ProgramTabFormKey.hrContact]: "",
  [ProgramTabFormKey.projectManager]: "",
  [ProgramTabFormKey.projectCoordinator]: "",
  [ProgramTabFormKey.salesCreditDetails]: [],
  [ProgramTabFormKey.additionalInvoiceContactId]: "",
  [ProgramTabFormKey.participantManagerId]: "",
  [ProgramTabFormKey.businessApprovalNumber]: "",
  [ProgramTabFormKey.additionalHRContactId]: "",
  [ProgramTabFormKey.deliveryLeadEngagementManagerId]: "",
  [ProgramTabFormKey.hiringManager]: "",
  [ProgramTabFormKey.dossierNumber]: "",
};

export const createProgramParticipantTabDefaultValue = {
  [ParticipantTabFormKey.prefix]: "",
  [ParticipantTabFormKey.firstName]: "",
  [ParticipantTabFormKey.lastName]: "",
  [ParticipantTabFormKey.preferredName]: "",
  [ParticipantTabFormKey.dateOfBirth]: "",
  [ParticipantTabFormKey.age]: "",
  [ParticipantTabFormKey.gender]: "",
  [ParticipantTabFormKey.spouse]: "",
  [ParticipantTabFormKey.educationLevel]: "",
  [ParticipantTabFormKey.mustStartByDate]: "",
  [ParticipantTabFormKey.primaryLanguage]: "",
  [ParticipantTabFormKey.socialFund]: "",
  [ParticipantTabFormKey.nisId]: "",
  [ParticipantTabFormKey.nextContactDate]: "",
  [ParticipantTabFormKey.projectedProgramEndDate]: "",
  [ParticipantTabFormKey.textSubscribedDate]: "",
  [ParticipantTabFormKey.textUnsubscribedDate]: "",
  [ParticipantTabFormKey.emailUnsubscribedDate]: "",
  [ParticipantTabFormKey.ageRange]: "",
  [ParticipantTabFormKey.personalId]: "",

  [ParticipantTabFormKey.primaryEmail]: "",
  [ParticipantTabFormKey.alternativeEmail]: "",
  [ParticipantTabFormKey.emailType1]: "",
  [ParticipantTabFormKey.emailType2]: "",
  [ParticipantTabFormKey.primaryNumber]: "",
  [ParticipantTabFormKey.primaryCountryCode]: "",
  [ParticipantTabFormKey.primaryIsoCode]: "",
  [ParticipantTabFormKey.alternativeNumber]: "",
  [ParticipantTabFormKey.alternativeCountryCode]: "",
  [ParticipantTabFormKey.alternativeIsoCode]: "",
  [ParticipantTabFormKey.concatType1]: "",
  [ParticipantTabFormKey.appendPhoneNumberCountryCode]: "",
  [ParticipantTabFormKey.appendPhoneNumberIsoCode]: "",
  [ParticipantTabFormKey.appendPhoneNumber]: "",
  [ParticipantTabFormKey.smsCheckBox]: false,
  [ParticipantTabFormKey.emailCheckBox]: false,
  [ParticipantTabFormKey.nextSms]: "",
  [ParticipantTabFormKey.nextEmail]: "",

  [ParticipantTabFormKey.address1]: "",
  [ParticipantTabFormKey.address2]: "",
  [ParticipantTabFormKey.address3]: "",
  [ParticipantTabFormKey.countryId]: "",
  [ParticipantTabFormKey.countryName]: "",
  [ParticipantTabFormKey.city]: "",
  [ParticipantTabFormKey.stateProvince]: "",
  [ParticipantTabFormKey.postalCode]: "",

  [ParticipantTabFormKey.eligibility]: OutreachEligible,
  [ParticipantTabFormKey.outreachEligibleStatus]: "",
  [ParticipantTabFormKey.assignedOutreachConsultant]: "",
  [ParticipantTabFormKey.notificationDate]: "",
  [ParticipantTabFormKey.releaseDate]: "",
  [ParticipantTabFormKey.outreachEligibleDate]: "",
  [ParticipantTabFormKey.notificationSupportDate]: "",
  [ParticipantTabFormKey.notificationSupportStatus]: "",
  [ParticipantTabFormKey.primaryManagedStatus]: "",
  [ParticipantTabFormKey.secondaryManagedStatus]: "",
  [ParticipantTabFormKey.contactWithin24Hours]: false,

  [ParticipantTabFormKey.clientName]: "",
  [ParticipantTabFormKey.jobTitle]: "",
  [ParticipantTabFormKey.employeeId]: "",
  [ParticipantTabFormKey.employeeBand]: "",
  [ParticipantTabFormKey.totalYearsEmployed]: "",
  [ParticipantTabFormKey.occupationalFunction]: "",
  [ParticipantTabFormKey.occupationalFunctionOther]: "",
  [ParticipantTabFormKey.occupationalLevel]: "",
  [ParticipantTabFormKey.occupationalLevelOther]: "",
  [ParticipantTabFormKey.planPostedDate]: "",
  [ParticipantTabFormKey.datePlanApproved]: "",
  [ParticipantTabFormKey.currentEmploymentStartDate]: "",
  [ParticipantTabFormKey.bestMatchPreference]: "",

  [ParticipantTabFormKey.targetJobTitle]: "",
  [ParticipantTabFormKey.targetOccupationFunctionId]: "",
  [ParticipantTabFormKey.targetOccupationOtherFunction]: "",
  [ParticipantTabFormKey.targetOccupationLevelId]: "",
  [ParticipantTabFormKey.targetIndustryId]: "",
  [ParticipantTabFormKey.targetOrganisation]: "",
  [ParticipantTabFormKey.targetJobTypeId]: "",
  [ParticipantTabFormKey.targetLocation]: "",
  [ParticipantTabFormKey.currenciesId]: "",
  [ParticipantTabFormKey.salaryRangeId]: "",
  [ParticipantTabFormKey.skills]: [],
  [ParticipantTabFormKey.technicalSkills]: [],
  [ParticipantTabFormKey.participantReadyToStartJobSearch]:
    programSiteCoreKeys.participantStartJobSearchNo,
  [ParticipantTabFormKey.participantJobSearchStartDate]: "",
  [ParticipantTabFormKey.participantNotSeekingEmployment]: false,
  [ParticipantTabFormKey.participantNotWillngToWorkRemotely]: false,
  [ParticipantTabFormKey.openToRelocation]: false,
  [ParticipantTabFormKey.participantWillingToTravelForWork]: false,
  [ParticipantTabFormKey.experis]: false,
  [ParticipantTabFormKey.manpowerEngineering]: false,
  [ParticipantTabFormKey.jeffersonWells]: false,
  [ParticipantTabFormKey.rpo]: false,
  // coachAssignment
  [CoachAssignmentFormKeys.toBeAssignDate]: "",
  [CoachAssignmentFormKeys.coachAssignments]: [],
  // Intake Selection
  [ParticipantTabFormKey.intakeStatusId]: "",
  [ParticipantTabFormKey.biosRequestedCoachIds]: [],
  [ParticipantTabFormKey.biosDeclinedCoachIds]: [],
  [ParticipantTabFormKey.bioStatusId]: "",
  [ParticipantTabFormKey.biosSentCoachIds]: [],
  [ParticipantTabFormKey.chemistryMeetingStatusId]: "",
  [ParticipantTabFormKey.chemistryMeetingSelectedCoachIds]: [],
  [ParticipantTabFormKey.coachSelectedCoachIds]: "",
  [ParticipantTabFormKey.isCoachSelectionDeclined]: false,
};

export const ProgramStatusFormKey = {
  programStatus: "programStatus",
  fromDate: "fromDate",
  throughDate: "throughDate",
  projectedProgramEndDate: "projectedProgramEndDate",
  notes: "notes",
} as const;

export const ProgramClosureFormKey = {
  primaryClosingReason: "primaryClosingReason",
  secondaryClosingReason: "secondaryClosingReason",
  closedDate: "closedDate",
  closedBy: "closedBy",
  landedDate: "landedDate",
  timeToLand: "timeToLand",
  totalHours: "totalHours",
  totalNumberOfMeetings: "totalNumberOfMeetings",
  geographicalMove: "geographicalMove",
  landingCountry: "landingCountry",
  state: "state",
  postalCode: "postalCode",
  notes: "notes",
  newEmployeeName: "newEmployeeName",
  newTitle: "newTitle",
  functionalChange: "functionalChange",
  contractType: "contractType",
  newOccupationalFunction: "newOccupationalFunction",
  newOccupationalFunctionOther: "newOccupationalFunctionOther",
  newOccupationalLevel: "newOccupationalLevel",
  industryChange: "industryChange",
  industryList: "industryList",
  newJobStartDate: "newJobStartDate",
  dateOfEndOfTrialPeriod: "dateOfEndOfTrialPeriod",
  solutionValidationDate: "solutionValidationDate",
  newEmploymentContractType: "newEmploymentContractType",
  compensationComparison: "compensationComparison",
  currentSalaryRange: "currentSalaryRange",
  newSalaryRange: "newSalaryRange",
  newEmployerVATNumber: "newEmployerVATNumber",
  placementMethod: "placementMethod",
  newLevel: "newLevel",
  workLocationType: "workLocationType",
  selectedPrimaryClosingReasonSitecoreKey:
    "selectedPrimaryClosingReasonSitecoreKey",
} as const;

export const ViewProgramStatusResultKey = {
  fromDate: "fromDate",
  throughDate: "throughDate",
  createdByName: "createdByName",
  createdTimeStamp: "createdTimeStamp",
  status: "status",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
} as const;

export type ProgramSectionKey = "programDetail" | "programOtherDetail";

export const AddActivityDefaultFormValue = {
  [AddActivityFormKey.chooseParticipant]: "",
  [AddActivityFormKey.activityType]: "",
  [AddActivityFormKey.modality]: "",
  [AddActivityFormKey.topic]: [],
  [AddActivityFormKey.notes]: "",
  [AddActivityFormKey.onBehalfOfCoachId]: "",
  [AddActivityFormKey.actualDurationInMinutes]: "",
  [AddActivityFormKey.defaultDuration]: "",
  [AddActivityFormKey.activityDate]: "",
};

export type ProgramFeeSectionKey = "feeDetails" | "taxDetails";

export const ProgramFeeTabFormKey = {
  id: "id",
  billingTypeId: "billingTypeId",
  billingTriggerId: "billingTriggerId",
  currencyId: "currencyId",
  fee: "fee",
  feeWithFormat: "feeWithFormat",
  earlyPayDays: "earlyPayDays",
  earlyPayPercentage: "earlyPayPercentage",
  minimumTransferFee: "minimumTransferFee",
  minimumTransferFeeWithFormat: "minimumTransferFeeWithFormat",
  proBonoCategoryId: "proBonoCategoryId",
  schedulePeriod: "schedulePeriod",
  feePaymentType: "feePaymentType",
  schedules: "schedules",
  milestones: "milestones",
  activities: "activities",
  feePercentage: "feePercentage",
  amount: "scheduleAmount",
  amountWithFormat: "scheduleAmountWithFormat",
  scheduleFrequency: "scheduleFrequency",
  billingSchedules: "billingSchedules",
  description: "description",
  status: "status",
  //Tax details
  taxSearch: "taxSearch",
  tax: "tax",
  taxPercentage: "percentage",
  taxAmount: "amount",
  taxAmountWithFormat: "amountWithFormat",
  taxEntries: "taxEntries",
  specialBillingInstructions: "specialBillingInstructions",
} as const;

export interface ProgramFeeTabFormType {
  id: string;
  billingTypeId: string;
  billingTriggerId?: string;
  currencyId: string;
  fee: string;
  feeWithFormat: string;
  earlyPayDays?: string;
  earlyPayPercentage?: string;
  minimumTransferFee?: string;
  minimumTransferFeeWithFormat?: string;
  proBonoCategoryId?: string;
  schedulePeriod?: string;
  feePaymentType?: string;
  schedules?: string;
  milestones?: string;
  activities?: BillingScheduleActivities[];
  feePercentage?: string;
  amount?: string;
  amountWithFormat?: string;
  scheduleFrequency?: string;
  billingSchedules?:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[];
  description?: string;
  status?: string;
  specialBillingInstructions?: string;
  //Tax details
  taxSearch?: string;
  tax?: string;
  taxPercentage?: string;
  taxAmount?: string;
  taxAmountWithFormat?: string;
  taxEntries?: TaxConfigureTableData[];
}

export const createProgramProgramFeeTabDefaultValue = {
  [ProgramFeeTabFormKey.id]: "",
  [ProgramFeeTabFormKey.billingTypeId]: "",
  [ProgramFeeTabFormKey.billingTriggerId]: "",
  [ProgramFeeTabFormKey.currencyId]: "",
  [ProgramFeeTabFormKey.fee]: "",
  [ProgramFeeTabFormKey.feeWithFormat]: "",
  [ProgramFeeTabFormKey.earlyPayDays]: "",
  [ProgramFeeTabFormKey.earlyPayPercentage]: "",
  [ProgramFeeTabFormKey.minimumTransferFee]: "",
  [ProgramFeeTabFormKey.minimumTransferFeeWithFormat]: "",
  [ProgramFeeTabFormKey.proBonoCategoryId]: "",
  [ProgramFeeTabFormKey.specialBillingInstructions]: "",
  [ProgramFeeTabFormKey.schedulePeriod]: "",
  [ProgramFeeTabFormKey.feePaymentType]: "",
  [ProgramFeeTabFormKey.schedules]: "",
  [ProgramFeeTabFormKey.milestones]: "",
  [ProgramFeeTabFormKey.activities]: [],
  [ProgramFeeTabFormKey.feePercentage]: "",
  [ProgramFeeTabFormKey.amount]: "",
  [ProgramFeeTabFormKey.amountWithFormat]: "",
  [ProgramFeeTabFormKey.scheduleFrequency]: "",
  [ProgramFeeTabFormKey.billingSchedules]: [],
  [ProgramFeeTabFormKey.description]: "",
  [ProgramFeeTabFormKey.status]: "",
  //Tax details
  [ProgramFeeTabFormKey.taxSearch]: "",
  [ProgramFeeTabFormKey.tax]: "",
  [ProgramFeeTabFormKey.taxPercentage]: "",
  [ProgramFeeTabFormKey.taxAmount]: "",
  [ProgramFeeTabFormKey.taxAmountWithFormat]: "",
  [ProgramFeeTabFormKey.taxEntries]: [],
};

export type ProgramInvoice = KeyValue;

export type ProgramFeeAndExpense = ProgramExpenseData & { isChecked: boolean };

export const PurchaseOrderKey = {
  poNumber: "poNumber",
  status: "statusId",
  effectiveDate: "effectiveDate",
  currentAmount: "currentAmount",
  expiryDate: "expiryDate",
};

export const InvoiceHistoryColumnKey = {
  number: "number",
  date: "date",
  statusId: "statusId",
  reversalReason: "reversalReason",
  totalInvoiceAmount: "totalInvoiceAmount",
  issuedBy: "issuedBy",
  purchaseOrder: "purchaseOrder",
} as const;

export const ViewInvoiceFeesAndExpenses = {
  name: "name",
  transactionDate: "transactionDate",
  typeId: "typeId",
  billingAmount: "billingAmount",
  taxAmount: "taxAmount",
  totalBillingAmount: "totalBillingAmount",
  expenseCategoryName: "expenseCategoryName",
  expenseTypeName: "expenseTypeName",
  lineNumber: "lineNumber",
} as const;

export const ProgramFessAndExpensesColumnKey = {
  isChecked: "isChecked",
  feesOrExpenseName: "name",
  transactionDate: "transactionDate",
  type: "typeName",
  category: "expenseCategoryName",
  feesOrExpenseType: "feeOrExpenseType",
  statusId: "statusId",
  unitRate: "unitRate",
  quantity: "quantity",
  feesOrExpenseAmount: "amount",
  billingAmount: "billingAmount",
  taxAmount: "tax",
  totalInvoiceAmount: "totalAmount",
  comment: "comments",
  status: "status",
} as const;

export const AddOrEditExpenseOrFeeFormKey = {
  type: "type",

  expenseIncurredById: "expenseIncurredById",
  expenseTypeId: "expenseTypeId",
  expenseCategoryId: "expenseCategoryId",
  expenseUnitPrice: "expenseUnitPrice",
  expenseQuantity: "expenseQuantity",

  transactionDate: "transactionDate",
  billingCurrency: "billingCurrency",
  billingCurrencyName: "billingCurrencyName",
  billingCurrencyExchangeRate: "billingCurrencyExchangeRate",
  billingAmount: "billingAmount",
  billingAmountWithFormat: "billingAmountWithFormat",
  comments: "comments",
  feeOrExpenseAmountWithFormat: "feeOrExpenseAmountWithFormat",
  feeOrExpenseCurrencyName: "feeOrExpenseCurrencyName",
  feeOrExpenseName: "feeOrExpenseName",
  feeOrExpenseCurrencyId: "feeOrExpenseCurrencyId",
  feeOrExpenseAmount: "feeOrExpenseAmount",
  feeOrExpenseFormattedAmount: "feeOrExpenseAmountFormatted",
  feeTypeId: "feeTypeId",
  taxSearch: "taxSearch",
  tax: "tax",
  taxPercentage: "percentage",
  taxAmount: "amount",
  taxAmountWithFormat: "amountWithFormat",
  taxEntries: "taxEntries",
  isRateConverted: "isRateConverted",
};

export interface MyForm {
  type: string;
  generalTab: KeyValue;
  taxTab: KeyValue;
}

export const ADD_PROGRAM_FEE_AND_EXPENSE_TYPES = {
  PROGRAM_OR_PROJECT_FEE:
    addFeesAndExpensesSiteCoreKeys.programOrProjectFeeType,
  PROGRAM_EXPENSE: addFeesAndExpensesSiteCoreKeys.expenseRadioButton,
  PROGRAM_FEE: addFeesAndExpensesSiteCoreKeys.programFeeType,
};

export const AddExpenseOrFeeSummeryColumns = {
  totalAmount: "totalAmount",
};

export interface FeesAndExpenseRow {
  id: string;
  transactionDate: string;
  typeName: string;
  typeId?: string;
  name: string;
  expenseCategoryName: string;
  currencyName: string;
  feeOrExpenseType: string;
  status: string;
  description: string;
  statusId: string;
  unitRate: number;
  billingAmount: number;
  quantity: number;
  amount: number;
  taxDetails: TaxDetail[];
  tax: number;
  totalAmount: number;
  comments: string;
  isChecked: boolean;
}

export const billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps =
  {
    billingTypeIdKey: ProgramFeeTabFormKey.billingTypeId,
    schedulePeriodKey: ProgramFeeTabFormKey.schedulePeriod,
    feeKey: ProgramFeeTabFormKey.fee,
    scheduleKey: ProgramFeeTabFormKey.schedules,
    feePercentageKey: ProgramFeeTabFormKey.feePercentage,
    amountWithFormatKey: ProgramFeeTabFormKey.amountWithFormat,
    amountKey: ProgramFeeTabFormKey.amount,
    scheduleFrequencyKey: ProgramFeeTabFormKey.scheduleFrequency,
    milestoneKey: ProgramFeeTabFormKey.milestones,
    activitiesKey: ProgramFeeTabFormKey.activities,
    billingSchedulesKey: ProgramFeeTabFormKey.billingSchedules,
    feePaymentTypeKey: ProgramFeeTabFormKey.feePaymentType,
    descriptionKey: ProgramFeeTabFormKey.description,
    statusKey: ProgramFeeTabFormKey.status,
    specialBillingInstructionsKey:
      ProgramFeeTabFormKey.specialBillingInstructions,
  };

export const taxDetailsFormKeys: TaxDetailsFormKeyProps = {
  taxSearchKey: ProgramFeeTabFormKey.taxSearch,
  programFeeKey: ProgramFeeTabFormKey.fee,
  taxKey: ProgramFeeTabFormKey.tax,
  taxPercentageKey: ProgramFeeTabFormKey.taxPercentage,
  taxAmountWithFormatKey: ProgramFeeTabFormKey.taxAmountWithFormat,
  taxAmountKey: ProgramFeeTabFormKey.taxAmount,
  taxEntriesKey: ProgramFeeTabFormKey.taxEntries,
};

export interface RaiseInvoiceFormType {
  feeOrExpenseName: string;
  type: string;
  category: string;
  feeOrExpenseType: string;
  billingAmount: string;
  taxAmount: string;
  totalInvoiceAmount: string;
  lineNumber: string;
}

export const RaiseInvoiceFormKey = {
  feeOrExpenseName: "feeOrExpenseName",
  type: "type",
  category: "category",
  feeOrExpenseType: "feeOrExpenseType",
  billingAmount: "billingAmount",
  taxAmount: "taxAmount",
  totalInvoiceAmount: "totalInvoiceAmount",
  lineNumber: "lineNumber",
};

export type ViewInvoiceFeesAndExpenseListItem = {
  name: string;
  transactionDate: string;
  typeId: string;
  category: string;
  feesOrExpenseType: string | null;
  billingAmount: number;
  taxAmount: number;
  totalBillingAmount: number;
  expenseCategories: IdNameList;
  expenseTypes: IdNameList;
  expenseCategoryName?: string;
  expenseTypeName?: string;
  lineNumber: string;
};

export interface PurchaseOrderSummary {
  id: string;
  number: string;
}

export interface ViewInvoiceResponse {
  id: string;
  programId: string;
  number: string;
  date: string;
  statusId: StatusBadgeType;
  feeOrExpenseAmount: number;
  totalTaxAmount: number;
  totalInvoiceAmount: number;
  purchaseOrders: PurchaseOrderSummary;
  feesOrExpenses: ViewInvoiceFeesAndExpenseListItem[];
  feeCurrency: IdNameList;
  paymentTerm: ProgramStatus;
  reversalReason?: string;
  reversalComments?: string;
  invoiceLanguage?: InvoicingLanguage;
  localInvoiceNumber?: string;
}

export interface IssuedByUser {
  firstName: string;
  lastName: string;
  email: string;
}

export interface ProgramInvoicePurchaseOrder {
  id: string;
  number: string;
}

export interface ProgramInvoiceItem {
  id: string;
  number: string;
  date: string;
  statusId: string;
  totalInvoiceAmount: number;
  issuedBy: IssuedByUser;
  purchaseOrder: ProgramInvoicePurchaseOrder;
  isInvoiceReversed: boolean;
  reversalReason: string;
  reversalComments?: string;
  hasRevenueSplits?: boolean;
}

export interface ProgramInvoiceSummary {
  totalAmount: number;
  invoices: ProgramInvoiceItem[];
  currency: IdNameList;
}

export const addExpenseFormInitialValue = {
  [AddOrEditExpenseOrFeeFormKey.type]:
    ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE,
  [AddOrEditExpenseOrFeeFormKey.expenseIncurredById]: "",
  [AddOrEditExpenseOrFeeFormKey.expenseTypeId]: "",
  [AddOrEditExpenseOrFeeFormKey.expenseCategoryId]: "",
  [AddOrEditExpenseOrFeeFormKey.expenseUnitPrice]: "",
  [AddOrEditExpenseOrFeeFormKey.expenseQuantity]: "",
  [AddOrEditExpenseOrFeeFormKey.transactionDate]: "",
  [AddOrEditExpenseOrFeeFormKey.billingCurrency]: "",
  [AddOrEditExpenseOrFeeFormKey.billingCurrencyName]: "",
  [AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate]: "",
  [AddOrEditExpenseOrFeeFormKey.billingAmount]: "",
  [AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat]: "",
  [AddOrEditExpenseOrFeeFormKey.comments]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyName]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseName]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount]: "",
  [AddOrEditExpenseOrFeeFormKey.feeOrExpenseFormattedAmount]: "",
  [AddOrEditExpenseOrFeeFormKey.feeTypeId]: "",
  [AddOrEditExpenseOrFeeFormKey.taxSearch]: "",
  [AddOrEditExpenseOrFeeFormKey.tax]: "",
  [AddOrEditExpenseOrFeeFormKey.taxPercentage]: "",
  [AddOrEditExpenseOrFeeFormKey.taxAmount]: "",
  [AddOrEditExpenseOrFeeFormKey.taxAmountWithFormat]: "",
  [AddOrEditExpenseOrFeeFormKey.taxEntries]: null,
};

export const RaiseInvoicePODetailsFormKey = {
  localInvoiceNumber: "localInvoiceNumber",
  poNumber: "poNumber",
  currentPOBalance: "currentPOBalance",
  poShortage: "poShortage",
  invoiceDate: "invoiceDate",
  invoiceLanguage: "invoiceLanguage",
  paymentTerms: "paymentTerms",
};

export const SalesCreditDetailsFormKeys = {
  clientRelationshipOwner: "clientRelationshipOwner",
  percentage: "percentage",
  officeName: "officeName",
} as const;

export interface SalesCreditTableData {
  id?: string;
  localClientRelationshipOwnerId?: string;
  clientRelationshipOwner?: string;
  officeName: string;
  firstName: string;
  lastName: string;
  email: string;
  percentage: string;
  [key: string]: any;
}

export const InvoiceReversalFormKey = {
  reversalReason: "reversalReason",
  reversalDate: "reversalDate",
  comments: "comments",
};

export interface InvoicingLanguage {
  code: string;
  id: string;
  name: string;
}

export interface GeneralInformationType {
  landedDate: string;
  timeToLandInDays: number;
  totalMeetings: number;
  geogrphicMove: string;
  landingCountry: string;
  notes: string;
  state?: string;
}

export interface EmployerInformationType {
  newTitle: string;
  functionalChange: string;
  newOccupationalFunction: string;
  newOccupationalFunctionText?: string;
  occupationalLevel: string;
  industryChange: string;
  industryList: string;
  newJobStartDate: string;
  compansationComparision: string;
  placementMethod: string;
  newEmployerName?: string;
  postalCode?: string;
  workLocationType?: string;
  endDateOfTrialPeriod?: string;
  newEmployerVAT?: string;
  newSalaryRange?: string;
  currentSalaryRange?: string;
  newLevel?: string;
  state?: string;
  newEmploymentContractType?: string;
  solutionValidatedDate?: string;
}

export interface PrimaryClosingReasonType {
  primaryClosingReasonStatus?: string[];
}

export interface ProgramTabFormHookType {
  prefix: string;
  firstName: string;
  lastName: string;
  programName: string;
  solutionOffering: string;
  programClass: string;
  businessUnit: string;
  costCentre: string;
  clientInformation1: string;
  clientInformation2: string;
  clientInformation3: string;
  site: string;
  poNumber: string;
  billingOffice: string;
  salesOffice: string;
  clientRelationShipOwner: string;
  deliveryOffice: string;
  billingContact: string;
  hrContact: string;
  projectManager: string;
  projectCoordinator: string;
  salesCreditDetails?: SalesCreditTableData[];
  additionalInvoiceContactId: string;
  participantManagerId: string;
  businessApprovalNumber: string;
  additionalHRContactId: string;
  deliveryLeadEngagementManagerId: string;
  hiringManager: string;
  dossierNumber?: string;
  actualClientId?: string;
}

export const AddCoachAssignmentFormKeys = {
  function: "function",
  coach: "coach",
} as const;
